import { atom, computed } from 'nanostores';
import type { SupabaseChangeData } from '.';
import { isCurrentChatUsingClaudeCode } from '~/lib/ai';

export class ChangeQueue {
  migrationsInDBFetched = atom(false);

  #queue = atom<SupabaseChangeData[]>([]);

  readonly hasMigrations = computed(this.#queue, (confirmationQueue) => {
    return confirmationQueue.some((item) => item.type === 'migration');
  });

  readonly firstItemInConfirmationQueue = computed(
    [this.#queue, this.migrationsInDBFetched],
    (confirmationQueue, migrationInDBFetched) => {
      const entry = confirmationQueue[0] as SupabaseChangeData | undefined;

      if (entry?.type === 'migration' && !migrationInDBFetched) {
        return undefined;
      }

      return entry;
    },
  );

  getChangesInQueue() {
    return this.#queue.get();
  }

  setState({ queue, migrationInDBFetched }: { queue: SupabaseChangeData[]; migrationInDBFetched?: boolean }) {
    this.#queue.set(queue);

    if (typeof migrationInDBFetched === 'boolean') {
      this.migrationsInDBFetched.set(migrationInDBFetched);
    }
  }

  injectConnectRequired() {
    if (isCurrentChatUsingClaudeCode()) {
      return;
    }

    if (this.#queue.get()[0]?.type === 'connect-required') {
      return;
    }

    // we ask the user to connect to supabase
    this.unshift({
      id: crypto.randomUUID(),
      type: 'connect-required' as const,
    });
  }

  find(changeId: string) {
    return this.#queue.get().find(({ id }) => id === changeId);
  }

  reset() {
    this.#queue.set([]);
    this.migrationsInDBFetched.set(false);
  }

  unshift(change: SupabaseChangeData) {
    const newConfirmationQueue = [change, ...this.#queue.get()];

    this.#queue.set(newConfirmationQueue);
  }

  remove(id: string) {
    const newConfirmationQueue = [...this.#queue.get()];
    const index = newConfirmationQueue.findIndex((info) => info.id === id);

    if (index === -1) {
      return;
    }

    newConfirmationQueue.splice(index, 1);

    this.#queue.set(newConfirmationQueue);
  }
}
